import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import DataTransferAPI from 'dashboard/api/dataTransfer';

// Limite de linhas da importação: um arquivo exportado acima disso precisa ser dividido para voltar
export const IMPORT_ROW_LIMIT = 1000;

export const downloadBlob = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
};

const fileNameFrom = (response, fallback) =>
  /filename="?([^";]+)"?/.exec(
    response.headers?.['content-disposition']
  )?.[1] || fallback;

/**
 * Importar/Exportar dados unificado (spec 146): o fluxo novo só aparece quando a conta tem a flag
 * `unified_data_transfer`; o download da exportação é imediato e sempre traz tudo da conta.
 */
export function useDataTransfer() {
  const { t } = useI18n();
  const { isCloudFeatureEnabled } = useAccount();
  const isUnified = computed(() =>
    isCloudFeatureEnabled(FEATURE_FLAGS.UNIFIED_DATA_TRANSFER)
  );
  const isExporting = ref(false);

  const exportAll = async () => {
    if (isExporting.value) return;
    isExporting.value = true;
    try {
      const response = await DataTransferAPI.export();
      const fallback = `corvus-dados-${new Date().toISOString().slice(0, 10)}.csv`;
      downloadBlob(response.data, fileNameFrom(response, fallback));
      useAlert(t('DATA_TRANSFER.EXPORT.SUCCESS'));
      const rows = Number(response.headers?.['x-data-transfer-rows']);
      if (rows > IMPORT_ROW_LIMIT) {
        useAlert(
          t('DATA_TRANSFER.EXPORT.TOO_MANY_ROWS', {
            count: rows,
            limit: IMPORT_ROW_LIMIT,
          })
        );
      }
    } catch (error) {
      const text = await error.response?.data?.text?.();
      let message = null;
      try {
        message = JSON.parse(text)?.error;
      } catch (parseError) {
        message = null;
      }
      useAlert(message || t('DATA_TRANSFER.EXPORT.ERROR'));
    } finally {
      isExporting.value = false;
    }
  };

  const downloadExample = async () => {
    try {
      const response = await DataTransferAPI.example();
      downloadBlob(response.data, 'corvus-exemplo-importacao.csv');
    } catch (error) {
      useAlert(t('DATA_TRANSFER.TUTORIAL.EXAMPLE_ERROR'));
    }
  };

  return { isUnified, isExporting, exportAll, downloadExample };
}
